import { useState } from "react";
import {
    Stepper,
    Step,
    StepLabel,
    Button,
    TextField,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { db, auth } from "../firebase-config";
import { doc, setDoc, getDoc, getDocs, updateDoc, arrayUnion, query, collection, where } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import "../style/QuestionnaireCreate.css";
import "../style/Global.css";

const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60000); // UTC time change to local
    return localDate.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
};      

function CreateQuestionnaire() {
    const [step, setStep] = useState(0);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [className, setClassName] = useState("");
    const [classId, setClassId] = useState(""); 
    const [questions, setQuestions] = useState([{ text: "", options: [], WeightFun: "group" }]);
    const [error, setError] = useState("");
    const [startDate, setStartDate] = useState(getCurrentDateTimeLocal());
    const [endDate, setEndDate] = useState("");
    const [studentsPerTeam, setStudentsPerTeam] = useState(3);
    const [enableFeedback, setEnableFeedback] = useState(true);
    const [enableTeamAllocationFeedback, setEnableTeamAllocationFeedback] = useState(true);
    const [teamAllocationFeedbackDuration, setTeamAllocationFeedbackDuration] = useState(7); // Default 7 days
    const [publishing, setPublishing] = useState(false);
    
    const navigate = useNavigate();

    const fetchClassId = async (className) => {
        try {
            const user = auth.currentUser;
            if (!user) return;
    
            // Query classes with matching name AND created by this teacher
            const q = query(
                collection(db, "Classes"),
                where("className", "==", className.toUpperCase()),
                where("teacherId", "==", user.uid)
            );
            
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                // Get the first matching class (assuming unique class names per teacher)
                const classData = querySnapshot.docs[0].data();
                return classData.classId;
            }
            return null;
        } catch (error) {
            console.error("Error fetching class ID:", error);
            return null;
        }
    };

    const handleNext = () => {
        // Can only proceed to the next step if the questionnaire is filled out
        if (step === 0 && !isValidQuestionnaire()) {
            setError(hasDuplicates 
                ? "Please remove duplicate options before submitting."
                : "All inputs must be filled out.");
            return;
        }
        setError("");
        setStep((prev) => prev + 1);
    };

    const handleBack = () => setStep((prev) => prev - 1);

    const addQuestion = () => {
        setQuestions([...questions, { text: "", options: [], WeightFun: "group" }]);
    };

    const deleteQuestion = (index) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const handleQuestionChange = (index, value) => {
        setQuestions((prevQuestions) => {
            const updatedQuestions = [...prevQuestions];
            updatedQuestions[index] = { ...updatedQuestions[index], text: value };
            return updatedQuestions;
        });
    };

    const handleWeightFunChange = (qIndex, value) => {
        setQuestions((prevQuestions) => {
            const updatedQuestions = [...prevQuestions];
            updatedQuestions[qIndex] = { ...updatedQuestions[qIndex], WeightFun: value };
            return updatedQuestions;
        });
    };

    const handleOptionChange = (qIndex, oIndex, value) => {
        setQuestions(prevQuestions => {
            const updatedQuestions = [...prevQuestions];
            updatedQuestions[qIndex].options[oIndex] = value;
            return updatedQuestions;
        });
    };

    const addOption = (qIndex) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].options.push("");
        setQuestions(updatedQuestions);
    };

    const removeOption = (qIndex, oIndex) => {
        const updatedQuestions = [...questions];
        updatedQuestions[qIndex].options = updatedQuestions[qIndex].options.filter((_, i) => i !== oIndex);
        setQuestions(updatedQuestions);
    };

    const hasDuplicates = questions.some(question => {
        const nonEmptyOptions = question.options.filter(opt => opt.trim() !== "");
        const uniqueOptions = new Set(nonEmptyOptions.map(opt => opt.trim().toLowerCase()));
        return nonEmptyOptions.length !== uniqueOptions.size;
    });
    // Checking if the questionnaire inputs are empty or not
    const isValidQuestionnaire = () => {
        const basicValidation = (
            title.trim() !== "" &&
            description.trim() !== "" &&
            className.trim() !== "" &&
            classId.trim() !== "" &&
            startDate.trim() !== "" &&
            endDate.trim() !== "" &&
            studentsPerTeam > 0 &&
            questions.every((q) => q.text.trim() !== "" && q.options.some((opt) => opt.trim() !== "")) &&
            !hasDuplicates
        );
    
        if (enableTeamAllocationFeedback) {
            return basicValidation && teamAllocationFeedbackDuration > 0;
        }
        return basicValidation;
    };


    const handlePublish = async () => {
        setError(""); // Reset error state
        setPublishing(true);

        if (!isValidQuestionnaire()) {
            const hasDuplicates = questions.some(question => {
                const nonEmptyOptions = question.options.filter(opt => opt.trim() !== "");
                const uniqueOptions = new Set(nonEmptyOptions.map(opt => opt.trim().toLowerCase()));
                return nonEmptyOptions.length !== uniqueOptions.size;
            });

            setError(hasDuplicates 
                ? "Please remove duplicate options before submitting."
                : "All inputs must be filled out.");
            return;
        }

        try {
            const user = auth.currentUser;
            const teacherId = user.uid; // Get teacher ID from the authenticated user

            // Check if a questionnaire already exists for this class
            const questionnaireQuery = query(
                collection(db, "Questionnaires"),
                where("classId", "==", classId)
            );
            const questionnaireSnapshot = await getDocs(questionnaireQuery);

            if (!questionnaireSnapshot.empty) {
                const existingQuestionnaire = questionnaireSnapshot.docs[0].data();
                setError(`A questionnaire titled "${existingQuestionnaire.title}" already exists for this class. 
                    Each class can only have one questionnaire.`);
                return;
            }

            // Generate unique ID for the questionnaire
            const questionnaireId = uuidv4();

            // Prepare date objects
            const selectedStart = new Date(startDate);
            const selectedEnd = new Date(endDate);
            const today = new Date();

            // Calculate team allocation feedback dates if enabled
            let teamAllocationFeedbackStart = null;
            let teamAllocationFeedbackEnd = null;
            
            if (enableTeamAllocationFeedback) {
                teamAllocationFeedbackStart = new Date(selectedEnd);
                teamAllocationFeedbackEnd = new Date(selectedEnd);
                teamAllocationFeedbackEnd.setDate(teamAllocationFeedbackEnd.getDate() + teamAllocationFeedbackDuration);
            }

            // Compare by date only
            const isStartToday =
                selectedStart.getFullYear() === today.getFullYear() &&
                selectedStart.getMonth() === today.getMonth() &&
                selectedStart.getDate() === today.getDate();

            // If the teacher selects Today, use Now as the start date. Otherwise, use select date
            const now = new Date();
            const actualStartDate = isStartToday
                ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds())
                : selectedStart;

            const status = actualStartDate <= now ? "active" : "inactive";

            // Save questionnaire inside the "Questionnaires" collection
            const questionnaireRef = doc(db, "Questionnaires", questionnaireId); // Use questionnaireId as doc ID
            await setDoc(questionnaireRef, {
                title,
                description,
                className: className.toUpperCase(), // Link questionnaire to class
                classId: classId,
                startDate: actualStartDate,
                endDate: selectedEnd,
                enableFeedback: enableFeedback,
                enableTeamAllocationFeedback: enableTeamAllocationFeedback,
                teamAllocationFeedbackStart, // Add these new fields
                teamAllocationFeedbackEnd,    // Add these new fields
                teamAllocationFeedbackDuration,
                questions,
                studentsPerTeam, // Save the number of students per team
                questionnaireId,
                createdBy: teacherId,
                createdAt: new Date(),
                status,
            });

            // Notify students if active immediately
            if (status === "active") {
                const classSnap = await getDoc(doc(db, "Classes", classId));
                if (classSnap.exists()) {
                    const students = classSnap.data().students || [];
                    const classLabel = classSnap.data().className || "your class";
                    for (const s of students) {
                        const userRef = doc(db, "Users", s.uid);
                        await updateDoc(userRef, {
                            notifications: arrayUnion({
                                message: `New questionnaire available in ${classLabel}: ${title}`,
                                timestamp: new Date(),
                                seen: false
                            })
                        });
                    }
                }
            }
            alert(`Questionnaire created successfully!`);

            navigate("/TeacherDashboard");
        } catch (error) {
            console.error("Error publishing questionnaire:", error);
            setError("Failed to publish. Please try again.");
        } finally {
            setPublishing(false);
        }
    };

    return (
        <div className="create-questionnaire-container">
            <Button
                variant="outlined"
                onClick={() => navigate("/ViewQuestionnaire")}    
            >
                Back
            </Button>

            <Stepper activeStep={step} alternativeLabel>
                {["Add Questions", "Preview", "Publish"].map((label, index) => (
                    <Step key={index} onClick={() => setStep(index)}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <div className="stepper-content">
                {step === 0 && (
                    <>
                        <TextField
                            fullWidth
                            label="Questionnaire Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Class Name"
                            value={className}
                            onChange={async (e) => {
                                const name = e.target.value;
                                setClassName(name);
                                // Fetch and set class ID when name changes
                                const id = await fetchClassId(name);
                                setClassId(id || "");
                                if (name && !id) {
                                    setError("Class not found. Please enter a valid class name.");
                                } else {
                                    setError("");
                                }
                            }}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Start Date and Time"
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="End Date and Time"
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Students Per Team"
                            type="number"
                            value={studentsPerTeam}
                            onChange={(e) => setStudentsPerTeam(Number(e.target.value))}
                            margin="normal"
                            inputProps={{ min: 1 }} // Ensure the value is at least 1
                        />
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={enableFeedback}
                                onChange={(e) => setEnableFeedback(e.target.checked)}
                            />
                            Enable Questionnaire Feedback (Will be available until the questionnaire has ended)
                        </label>
                        
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={enableTeamAllocationFeedback}
                                onChange={(e) => setEnableTeamAllocationFeedback(e.target.checked)}
                            />
                            Enable Team Allocation Feedback
                        </label>

                        {enableTeamAllocationFeedback && (
                            <TextField
                                fullWidth
                                label="Team Allocation Feedback Duration (number of days after questionnaire has ended)"
                                type="number"
                                value={teamAllocationFeedbackDuration}
                                onChange={(e) => setTeamAllocationFeedbackDuration(Number(e.target.value))}
                                margin="normal"
                                inputProps={{ min: 1 }} // Ensure at least 1 day
                                className="feedback-duration-field"
                            />
                        )}

                        {questions.map((question, qIndex) => (
                            <div key={qIndex} className="question-container">
                                <TextField
                                    fullWidth
                                    label="Question"
                                    value={question.text}
                                    onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
                                    margin="normal"
                                />

                                <strong>Weight Function:</strong>
                                <RadioGroup
                                    value={question.WeightFun}
                                    onChange={(e) => handleWeightFunChange(qIndex, e.target.value)}
                                    row
                                >
                                    <FormControlLabel value="group" control={<Radio />} label="Group" />
                                    <FormControlLabel value="balance" control={<Radio />} label="Balance" />
                                </RadioGroup>

                                {question.options?.map((option, oIndex) => {
                                    const isDuplicate = !isValidQuestionnaire() && 
                                        question.options.some((opt, idx) => 
                                            idx !== oIndex && 
                                            opt.trim().toLowerCase() === option.trim().toLowerCase() && 
                                            option.trim() !== ""
                                        );

                                    return (
                                        <div key={oIndex} className="option-container">
                                            <TextField
                                                value={option}
                                                onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                                                placeholder={`Option ${oIndex + 1}`}
                                                variant="outlined"
                                                size="small"
                                                error={isDuplicate}
                                                helperText={isDuplicate ? "Duplicate option" : ""}
                                                className="option-textfield"
                                            />
                                            <IconButton 
                                                onClick={() => removeOption(qIndex, oIndex)}
                                                color="error"
                                                size="small"
                                                style={{ marginLeft: '8px' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                    );
                                })}
                                <Button startIcon={<AddCircleIcon />} onClick={() => addOption(qIndex)}>
                                    Add Option
                                </Button>

                                <IconButton onClick={() => deleteQuestion(qIndex)} style={{ marginLeft: "10px" }}>
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        ))}

                        <Button variant="contained" onClick={addQuestion} className="add-question-button">
                            Add Question
                        </Button>
                    </>
                )}

                {step === 1 && (
                    <div className="preview-section">
                        <h3 className="preview-header">Preview</h3>
                        <div className="preview-metadata">
                            <h4 className="preview-title">{title}</h4>
                            <p className="preview-description">{description}</p>

                            <div className="metadata-grid">
                                <div className="metadata-item">
                                    <span className="metadata-label">Start Date:</span>
                                    <span className="metadata-value">{new Date(startDate).toLocaleString()}</span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">End Date:</span>
                                    <span className="metadata-value">{new Date(endDate).toLocaleString()}</span>
                                </div>
                                <div className="metadata-item">
                                    <span className="metadata-label">Students Per Team:</span>
                                    <span className="metadata-value">{studentsPerTeam}</span>
                                </div>
                                {enableTeamAllocationFeedback && (
                                    <>
                                        <div className="metadata-item">
                                            <span className="metadata-label">Team Feedback Duration:</span>
                                            <span className="metadata-value">{teamAllocationFeedbackDuration} days</span>
                                        </div>
                                        <div className="metadata-item">
                                            <span className="metadata-label">Team Allocation Feedback Period:</span>
                                            <span className="metadata-value">
                                                {new Date(endDate).toLocaleString()} to<br />
                                                {new Date(new Date(endDate).getTime() + teamAllocationFeedbackDuration * 24 * 60 * 60 * 1000).toLocaleString()}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="questions-container">
                            {questions.map((q, index) => (
                                <div key={index} className="question-card">
                                    <div className="question-header">
                                        <h5 className="question-text">{q.text}</h5>
                                        <span className={`weight-tag weight-${q.WeightFun}`}>
                                            {q.WeightFun}
                                        </span>
                                    </div>
                                    <ul className="options-list">
                                        {q.options.map((opt, idx) => opt && (
                                            <li key={idx} className="option-item">
                                                <span className="option-bullet"></span>
                                                <span className="option-text">{opt}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="publish-section">
                        <h3 className="publish-header">Are you sure you want to publish this questionnaire?</h3>
                        <Button variant="contained" onClick={handlePublish} disabled={!isValidQuestionnaire() || publishing}>
                            {publishing ? "Publishing..." : "Publish Questionnaire"}
                        </Button>
                    </div>
                )}

                <div className="navigation-buttons">
                    {error && <p className="error-text">{error}</p>}
                    {step > 0 && <Button onClick={handleBack}>Back</Button>}
                    {step < 2 && <Button onClick={handleNext}>Next</Button>}
                </div>
            </div>
        </div>
    );
}

export default CreateQuestionnaire;